import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Play, Pause, Upload, Trash2, LogOut, Lock, X, Film } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CustomCursor from '../components/CustomCursor';
import MouseGlow from '../components/MouseGlow';
import { localizedPath } from '../utils/routes';
import { usePageTransition } from '../components/PageTransition';
import { SEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['all', 'advertising', 'social', 'branding', 'presentation'];

const VideoPlayer = ({ video }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) { videoRef.current.pause(); } else { videoRef.current.play(); }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="relative aspect-video bg-black rounded-xl overflow-hidden border border-white/10 group-hover:border-[#FFD700]/30 transition-all duration-500 cursor-pointer" onClick={togglePlay}>
      <video ref={videoRef} src={`${API_URL}/api/videos/${video.id}/file`} className="w-full h-full object-cover" loop playsInline preload="metadata" />
      <AnimatePresence>
        {!isPlaying && (
          <motion.div className="absolute inset-0 flex items-center justify-center bg-black/40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="w-16 h-16 rounded-full border-2 border-[#FFD700] flex items-center justify-center bg-black/60 backdrop-blur-sm" whileHover={{ scale: 1.1 }}>
              <Play className="w-6 h-6 text-[#FFD700] ml-1" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {isPlaying && (
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <Pause className="w-5 h-5 text-white/70" />
        </div>
      )}
    </div>
  );
};

const AdminPanel = ({ onVideoUploaded, t }) => {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('advertising');
  const [videos, setVideos] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetch(`${API_URL}/api/videos`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => { if (r.ok) { setIsLoggedIn(true); return r.json(); } throw new Error(); })
        .then(setVideos)
        .catch(() => { setIsLoggedIn(false); localStorage.removeItem('admin_token'); setToken(''); });
    }
  }, [token]);

  const handleLogin = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password })
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setToken(data.token);
      localStorage.setItem('admin_token', data.token);
      setIsLoggedIn(true);
    } catch { alert('Password errata'); }
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress('Caricamento...');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title || file.name);
    formData.append('description', description);
    formData.append('category', category);
    try {
      const res = await fetch(`${API_URL}/api/admin/videos/upload?title=${encodeURIComponent(title || file.name)}&description=${encodeURIComponent(description)}&category=${encodeURIComponent(category)}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Upload failed'); }
      setUploadProgress('Video caricato!');
      setTitle(''); setDescription(''); setCategory('advertising');
      if (fileRef.current) fileRef.current.value = '';
      const listRes = await fetch(`${API_URL}/api/videos`);
      const list = await listRes.json();
      setVideos(list);
      onVideoUploaded?.();
    } catch (e) { setUploadProgress(`Errore: ${e.message}`); }
    finally { setTimeout(() => { setUploading(false); setUploadProgress(''); }, 2000); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo video?')) return;
    await fetch(`${API_URL}/api/admin/videos/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    setVideos(prev => prev.filter(v => v.id !== id));
    onVideoUploaded?.();
  };

  if (!isLoggedIn) {
    return (
      <div className="max-w-sm mx-auto">
        <div className="border border-white/10 rounded-xl p-8 bg-white/[0.02]">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-5 h-5 text-[#FFD700]" />
            <h3 className="font-display text-xl text-white">{t('ai_video.admin_title')}</h3>
          </div>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()}
            placeholder={t('ai_video.admin_password')} data-testid="admin-password-input"
            className="w-full bg-black/50 border border-white/10 text-white px-4 py-3 rounded-lg mb-4 focus:border-[#FFD700]/50 outline-none text-sm" />
          <button onClick={handleLogin} data-testid="admin-login-btn"
            className="w-full py-3 bg-[#FFD700] text-black font-display text-sm tracking-widest hover:bg-[#FFD700]/90 transition-colors rounded-lg">
            {t('ai_video.admin_login')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-white/10 rounded-xl p-6 md:p-8 bg-white/[0.02]">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Film className="w-5 h-5 text-[#FFD700]" />
          <h3 className="font-display text-xl text-white">{t('ai_video.admin_title')}</h3>
        </div>
        <button onClick={() => { setIsLoggedIn(false); setToken(''); localStorage.removeItem('admin_token'); }}
          className="text-white/40 hover:text-white text-xs flex items-center gap-1">
          <LogOut className="w-3.5 h-3.5" /> {t('ai_video.admin_logout')}
        </button>
      </div>

      {/* Upload form */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder={t('ai_video.admin_title_field')} data-testid="video-title-input"
          className="bg-black/50 border border-white/10 text-white px-4 py-2.5 rounded-lg focus:border-[#FFD700]/50 outline-none text-sm" />
        <select value={category} onChange={e => setCategory(e.target.value)} data-testid="video-category-select"
          className="bg-black/50 border border-white/10 text-white px-4 py-2.5 rounded-lg focus:border-[#FFD700]/50 outline-none text-sm">
          <option value="advertising">Pubblicità</option>
          <option value="social">Social Media</option>
          <option value="branding">Branding</option>
          <option value="presentation">Presentazioni</option>
        </select>
      </div>
      <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={t('ai_video.admin_description_field')} rows={2} data-testid="video-description-input"
        className="w-full bg-black/50 border border-white/10 text-white px-4 py-2.5 rounded-lg focus:border-[#FFD700]/50 outline-none text-sm mb-4 resize-none" />
      <div className="flex gap-3 items-center">
        <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm" data-testid="video-file-input"
          className="flex-1 text-sm text-white/50 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border file:border-[#FFD700]/30 file:text-[#FFD700] file:bg-transparent file:text-xs file:tracking-wider file:uppercase file:cursor-pointer" />
        <button onClick={handleUpload} disabled={uploading} data-testid="video-upload-btn"
          className="flex items-center gap-2 px-6 py-2.5 bg-[#FFD700] text-black font-display text-xs tracking-widest rounded-lg hover:bg-[#FFD700]/90 disabled:opacity-50 transition-all">
          <Upload className="w-4 h-4" />
          {uploading ? t('ai_video.admin_uploading') : t('ai_video.admin_upload')}
        </button>
      </div>
      {uploadProgress && <p className="text-[#FFD700] text-sm mt-3">{uploadProgress}</p>}

      {/* Existing videos */}
      {videos.length > 0 && (
        <div className="mt-8 border-t border-white/10 pt-6">
          <div className="space-y-3">
            {videos.map(v => (
              <div key={v.id} className="flex items-center justify-between py-3 px-4 border border-white/5 rounded-lg">
                <div>
                  <span className="text-white text-sm">{v.title}</span>
                  <span className="text-white/30 text-xs ml-3">{v.category}</span>
                </div>
                <button onClick={() => handleDelete(v.id)} className="text-red-400/60 hover:text-red-400 transition-colors" data-testid={`delete-video-${v.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AIVideoPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const currentLang = lang || 'it';
  const { transitionTo } = usePageTransition();
  const [activeFilter, setActiveFilter] = useState('all');
  const [videos, setVideos] = useState([]);
  const [showAdmin, setShowAdmin] = useState(false);

  const loadVideos = () => {
    fetch(`${API_URL}/api/videos`).then(r => r.json()).then(setVideos).catch(() => {});
  };

  useEffect(() => { loadVideos(); }, []);

  const filterKey = (cat) => `filter_${cat === 'all' ? 'all' : cat}`;
  const filtered = activeFilter === 'all' ? videos : videos.filter(v => v.category === activeFilter);

  return (
    <div className="bg-[#0a0a0a] relative film-grain overflow-x-hidden min-h-screen">
      <SEO page="ai-video" />
      <MouseGlow />
      <CustomCursor />
      <Navbar />

      <main className="relative pt-32 pb-20" style={{ zIndex: 1 }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <motion.div className="mb-16" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-6 block">{t('ai_video.page_subtitle')}</span>
            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl text-white leading-[0.9] mb-6">
              {t('ai_video.page_title')} <span className="text-outline">{t('ai_video.page_title_outline')}</span>
            </h1>
            <p className="text-white/70 text-lg font-light max-w-xl">{t('ai_video.page_description')}</p>
          </motion.div>

          {/* Filters */}
          <motion.div className="flex flex-wrap gap-3 mb-16" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            {CATEGORIES.map(cat => (
              <button key={cat} onClick={() => setActiveFilter(cat)} data-testid={`video-filter-${cat}`}
                className={`px-6 py-3 text-xs tracking-[0.2em] uppercase transition-all duration-400 border ${activeFilter === cat ? 'bg-[#FFD700] text-black border-[#FFD700] font-bold' : 'bg-transparent text-white/60 border-white/15 hover:border-[#FFD700]/50 hover:text-white'}`}>
                {t(`ai_video.${filterKey(cat)}`)}
              </button>
            ))}
          </motion.div>

          {/* Video Grid */}
          <AnimatePresence mode="wait">
            <motion.div key={activeFilter} className="grid md:grid-cols-2 gap-8 lg:gap-10" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}>
              {filtered.length === 0 ? (
                <div className="col-span-2 py-20 flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-full border border-[#FFD700]/20 flex items-center justify-center">
                    <Play className="w-7 h-7 text-[#FFD700]/40" />
                  </div>
                  <p className="text-white/30 text-sm tracking-wider uppercase">{t('ai_video.no_videos')}</p>
                </div>
              ) : (
                filtered.map((video, index) => (
                  <motion.div key={video.id} className="group" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}
                    data-testid={`video-card-${video.id}`}>
                    <VideoPlayer video={video} />
                    <div className="mt-4 px-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[#FFD700] text-[10px] tracking-[0.3em] uppercase">{video.category}</span>
                        <span className="text-white/20 text-xs">{new Date(video.created_at).toLocaleDateString()}</span>
                      </div>
                      <h3 className="font-display text-2xl text-white mt-1 group-hover:text-[#FFD700] transition-colors duration-500">{video.title}</h3>
                      {video.description && <p className="text-white/40 font-light text-sm mt-1">{video.description}</p>}
                    </div>
                  </motion.div>
                ))
              )}
            </motion.div>
          </AnimatePresence>

          {/* Request CTA */}
          <motion.div className="mt-24 text-center" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <div className="w-16 h-px bg-[#FFD700]/30 mx-auto mb-8" />
            <h3 className="font-display text-3xl text-white mb-3">{t('ai_video.request_video')}</h3>
            <p className="text-white/50 text-base font-light mb-8 max-w-md mx-auto">{t('ai_video.request_description')}</p>
            <motion.button onClick={() => { navigate(localizedPath(currentLang, 'home')); setTimeout(() => { const el = document.getElementById('contatti') || document.getElementById('contact') || document.getElementById('kontakt'); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' }); }, 500); }}
              className="px-10 py-4 bg-[#FFD700] text-black font-display text-sm tracking-widest hover:bg-[#FFD700]/90 transition-colors"
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} data-testid="video-request-cta">
              {t('ai_video.request_cta')}
            </motion.button>
          </motion.div>

          {/* Admin toggle */}
          <div className="mt-20 flex justify-center">
            <button onClick={() => setShowAdmin(!showAdmin)} className="text-white/10 hover:text-white/30 text-[10px] tracking-widest uppercase transition-colors" data-testid="admin-toggle-btn">
              {showAdmin ? <X className="w-4 h-4 inline mr-1" /> : <Lock className="w-3 h-3 inline mr-1" />}
              {showAdmin ? 'Chiudi' : 'Admin'}
            </button>
          </div>

          {showAdmin && (
            <motion.div className="mt-6 max-w-2xl mx-auto" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <AdminPanel onVideoUploaded={loadVideos} t={t} />
            </motion.div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AIVideoPage;
